import React, { useState } from 'react'
import { Check, Copy, Loader2, Lock, Mail, Trash2, UserPlus, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../components/ui/PageHeader'
import { useTenant } from '../hooks/useTenant'
import { useMembers, useInviteMember, useRemoveMember, type TenantMember } from '../hooks/useMembers'

// ---------------------------------------------------------------------------
// Paywall
// ---------------------------------------------------------------------------

function GrowthPaywall() {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
      <div className="p-4 rounded-2xl bg-indigo-500/10 border border-indigo-500/20">
        <Lock size={28} className="text-indigo-400" />
      </div>
      <h2 className="text-lg font-semibold text-[--km-text-primary]">Funzionalità Growth</h2>
      <p className="text-sm text-[--km-text-muted] max-w-xs">
        La gestione dei collaboratori è disponibile dal piano Growth in su.
      </p>
      <Link
        to="/upgrade"
        className="mt-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors"
      >
        Scopri i piani
      </Link>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
  active:  'Attivo',
  pending: 'In attesa',
  revoked: 'Revocato',
  expired: 'Scaduto',
}

const STATUS_CLASSES: Record<string, string> = {
  active:  'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  revoked: 'bg-red-500/10 text-red-400 border-red-500/20',
  expired: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
}

// ---------------------------------------------------------------------------
// Invite form
// ---------------------------------------------------------------------------

const ROLES = ['CFO', 'Sales', 'Amministrativo', 'Produzione'] as const

interface InviteFormProps {
  onDone: (link: string | null) => void
}

function InviteForm({ onDone }: InviteFormProps) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>('Sales')
  const [error, setError] = useState<string | null>(null)
  const invite = useInviteMember()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const emailLower = email.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailLower)) {
      setError('Inserisci un indirizzo email valido.')
      return
    }
    try {
      const result = await invite.mutateAsync({ email: emailLower, role })
      onDone(result.email_sent ? null : (result.invite_link ?? null))
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore durante l\'invio'
      setError(msg)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
      {error && (
        <p className="text-xs text-[--km-error] bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="email@azienda.com"
          className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-[--km-text-primary] placeholder:text-[--km-text-disabled] focus:outline-none focus:border-indigo-500/50"
          required
        />
        <select
          value={role}
          onChange={e => setRole(e.target.value)}
          className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-indigo-500/50"
        >
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button
          type="submit"
          disabled={invite.isPending}
          className="flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 transition-colors"
        >
          {invite.isPending ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
          Invita
        </button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Copy link fallback
// ---------------------------------------------------------------------------

function CopyLinkBanner({ link, onClose }: { link: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Mail size={14} className="text-amber-400 shrink-0" />
        <p className="text-sm text-amber-400 font-medium">SMTP non configurato — copia il link da condividere manualmente</p>
      </div>
      <div className="flex gap-2">
        <input
          readOnly
          value={link}
          className="flex-1 rounded-lg bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-[--km-text-muted] font-mono"
        />
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-400 text-xs px-3 py-1.5 transition-colors"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copiato' : 'Copia'}
        </button>
        <button onClick={onClose} className="text-xs text-[--km-text-muted] hover:text-[--km-text-primary] px-2">×</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Remove dialog
// ---------------------------------------------------------------------------

function RemoveDialog({ member, onConfirm, onCancel }: { member: TenantMember; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="rounded-2xl border border-white/10 bg-[--km-surface] p-6 max-w-sm w-full mx-4 shadow-xl">
        <h3 className="text-base font-semibold text-[--km-text-primary] mb-2">
          {member.status === 'pending' ? 'Annulla invito?' : 'Rimuovi membro?'}
        </h3>
        <p className="text-sm text-[--km-text-muted] mb-5">
          Stai per revocare l&apos;accesso di <strong>{member.email}</strong> ({member.role}).
          {member.status === 'active' && ' Il membro perderà l\'accesso alla prossima richiesta.'}
        </p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 rounded-xl text-sm text-[--km-text-muted] hover:text-[--km-text-primary] transition-colors">
            Annulla
          </button>
          <button onClick={onConfirm} className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-colors">
            Rimuovi {member.status === 'pending' ? 'invito' : 'membro'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function CollaboratorsPage() {
  const { data: tenant, isLoading: tenantLoading } = useTenant()
  const { data: members, isLoading, isError } = useMembers()
  const remove = useRemoveMember()

  const [pendingLink, setPendingLink] = useState<string | null>(null)
  const [removing, setRemoving] = useState<TenantMember | null>(null)

  if (tenantLoading) {
    return <div className="flex justify-center pt-24"><Loader2 size={24} className="animate-spin text-indigo-400" /></div>
  }

  const plan = tenant?.plan ?? 'trial'
  if (!['growth', 'enterprise'].includes(plan)) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Collaboratori" subtitle="Gestione accessi e ruoli del team" />
        <GrowthPaywall />
      </div>
    )
  }

  async function handleRemove(member: TenantMember) {
    await remove.mutateAsync(member.id)
    setRemoving(null)
  }

  return (
    <div className="flex flex-col gap-6 pb-10">
      <PageHeader title="Collaboratori" subtitle="Invita il tuo team e assegna ruoli con accesso granulare" />

      {/* Form invito */}
      <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-5 flex flex-col gap-4">
        <h2 className="text-sm font-semibold text-[--km-text-primary] flex items-center gap-2">
          <UserPlus size={15} className="text-indigo-400" />
          Invita collaboratore
        </h2>
        <InviteForm onDone={link => link && setPendingLink(link)} />
        {pendingLink && <CopyLinkBanner link={pendingLink} onClose={() => setPendingLink(null)} />}
      </div>

      {/* Tabella collaboratori */}
      <div className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-4 border-b border-white/8 flex items-center gap-2">
          <Users size={15} className="text-[--km-text-muted]" />
          <h2 className="text-sm font-semibold text-[--km-text-primary]">
            Team {members ? `(${members.length})` : ''}
          </h2>
        </div>

        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 size={20} className="animate-spin text-indigo-400" />
          </div>
        )}

        {isError && (
          <p className="text-center py-12 text-sm text-[--km-error]">Errore caricamento collaboratori.</p>
        )}

        {!isLoading && !isError && members?.length === 0 && (
          <p className="text-center py-12 text-sm text-[--km-text-muted]">
            Nessun collaboratore ancora — invitane uno sopra.
          </p>
        )}

        {!isLoading && !isError && members && members.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-[--km-text-disabled] text-xs uppercase tracking-wide">
                <th className="px-5 py-3 text-left">Email</th>
                <th className="px-5 py-3 text-left">Ruolo</th>
                <th className="px-5 py-3 text-left">Stato</th>
                <th className="px-5 py-3 text-left">Invitato il</th>
                <th className="px-5 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {members.map(m => (
                <tr key={m.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-3 text-[--km-text-primary] font-medium">{m.email}</td>
                  <td className="px-5 py-3 text-[--km-text-muted]">{m.role}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${STATUS_CLASSES[m.status] ?? ''}`}>
                      {STATUS_LABELS[m.status] ?? m.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[--km-text-muted] text-xs">
                    {new Date(m.invited_at).toLocaleDateString('it-IT')}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {m.status !== 'revoked' && (
                      <button
                        onClick={() => setRemoving(m)}
                        className="p-1.5 rounded-lg text-[--km-text-muted] hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title={m.status === 'pending' ? 'Annulla invito' : 'Rimuovi membro'}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {removing && (
        <RemoveDialog
          member={removing}
          onConfirm={() => handleRemove(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  )
}
